/**
 * Application state: the board, and the theme.
 *
 * **There is no session here, and there is nowhere on this origin that there
 * could be one.** website-cloud's equivalent resolves who you are before it
 * renders anything; this one never asks. The public page's whole job is to work
 * for a stranger, and a visitor's browser makes exactly one request — the
 * summary — so nothing in the network tab hints that an account system exists
 * anywhere.
 *
 * The developer area used to be the exception, doing its own check when
 * somebody navigated to it. It is internal.mauvely.com now, so the exception
 * is gone with it.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, api, errorText, type SummaryWire } from './api';

/** How often the board refreshes itself.
 *
 *  Thirty seconds, against a ten-second edge cache, so a tab left open during an
 *  incident is never more than forty seconds stale — and a page full of people
 *  watching an outage costs the origin one request per ten seconds in total
 *  rather than one per viewer. */
const REFRESH_MS = 30_000;

export type BoardStatus = 'loading' | 'ready' | 'error';

interface AppState {
  status: BoardStatus;
  summary: SummaryWire | null;
  error: string | null;
  /** The machine-readable code, so a page can act on a specific failure rather
   *  than only print its sentence. `migrations_pending` is the one that earns
   *  its own screen. */
  errorCode: string | null;
  /** Set while a background refresh is in flight, so the header can show it
   *  without the page blanking — a status board that flashes a spinner every
   *  thirty seconds is unreadable. */
  refreshing: boolean;
  refresh: () => Promise<void>;
  theme: 'light' | 'dark';
  setTheme: (t: 'light' | 'dark') => void;
}

const AppContext = createContext<AppState | null>(null);

const THEME_KEY = 'mauvely-status-theme';

function initialTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Private mode, or storage disabled. The OS preference is a fine answer.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [summary, setSummary] = useState<SummaryWire | null>(null);
  const [status, setStatus] = useState<BoardStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [theme, setThemeState] = useState<'light' | 'dark'>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: 'light' | 'dark') => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Not worth failing over — the attribute is already set.
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const next = await api.summary();
      setSummary(next);
      setStatus('ready');
      setError(null);
      setErrorCode(null);
    } catch (err) {
      // A failed *refresh* keeps the last good board on screen. Replacing a
      // working page with an error because one poll failed is the worst thing
      // this component could do during the event it exists for.
      setError(errorText(err, 'Could not reach the status API.'));
      setErrorCode(err instanceof ApiError ? err.code : null);
      setStatus((s) => (s === 'ready' ? 'ready' : 'error'));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => {
      // A background tab does not need to poll. Cheap for us and for the
      // visitor's battery, and the visibility change below catches them up the
      // instant they look again.
      if (document.visibilityState === 'visible') void refresh();
    }, REFRESH_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const value = useMemo<AppState>(
    () => ({ status, summary, error, errorCode, refreshing, refresh, theme, setTheme }),
    [status, summary, error, errorCode, refreshing, refresh, theme, setTheme]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp used outside AppProvider');
  return ctx;
}
